import { isEn, t } from "../i18n";
import { castName, PORTRAIT, pimg } from "../art/cast";
import { $, h, prefersReducedMotion } from "../lib/dom";
import { L, rt, S } from "./state";
import { clock } from "./time";
import type { LogEntry, LogKind } from "./types";
import { entFor } from "./world";

export const chName = (ch: string): string => L.chName?.[ch] ?? t("log.scene");

interface PostOpts {
  /** Don't toast even when the channel isn't open. */
  silent?: boolean;
  /** Don't raise a "!" over the speaker. */
  noflag?: boolean;
}

const TOASTED: ReadonlySet<LogKind> = new Set(["npc", "alert", "result"]);

/** Append to the session log; shows it in the open dialog or as a toast. */
export function post(ch: string, kind: LogKind, who: string | null, text: string, opt: PostOpts = {}): LogEntry {
  const e: LogEntry = { ch, kind, who, text, t: S.t };
  S.log.push(e);
  const open = S.dialog !== null && L.dialogs[S.dialog]?.ch === ch;
  if (open || !S.dialog) appendLog(e, true);
  if (!opt.silent && !open && (TOASTED.has(kind) || (kind === "narr" && ch === "sys"))) toast(e);
  if (!open && who && TOASTED.has(kind) && !opt.noflag) {
    const en = entFor(who);
    if (en && S.flags[en.id] !== "?") S.flags[en.id] = "!";
  }
  return e;
}

/** The clerk's own line. */
export const me = (ch: string, txt: string): LogEntry => post(ch, "me", null, txt);

export function appendLog(e: LogEntry, animate: boolean): void {
  const l = $("#log");
  const d = h("div", { className: `msg ${e.kind}` });
  const nm = e.kind === "me" ? t("log.you") : e.kind === "narr" ? "" : castName(e.who);
  const pre = !S.dialog ? t("log.chan", { ch: chName(e.ch) }) : "";
  const sep = t("log.sep");
  const who = h("div", {
    className: "who" + (e.kind === "narr" ? " ch" : ""),
    text: pre + (e.kind === "result" ? t("log.result") + sep : "") + nm + (nm || pre ? sep : "") + clock(e.t),
  });
  if (e.kind !== "narr" || !S.dialog) d.appendChild(who);
  const tx = h("div", { className: "txt" });
  d.appendChild(tx);
  l.appendChild(d);
  const typed = animate && !rt.fastForward && !prefersReducedMotion() && e.kind === "npc" && S.dialog;
  if (!typed) {
    tx.textContent = e.text;
    l.scrollTop = l.scrollHeight;
    return;
  }
  let i = 0;
  const step = (): void => {
    if (rt.fastForward || !d.isConnected) {
      tx.textContent = e.text;
      return;
    }
    // Latin text runs about three times the characters of the same line in Chinese.
    i += isEn ? 5 : 2;
    tx.textContent = e.text.slice(0, i);
    l.scrollTop = l.scrollHeight;
    if (i < e.text.length) setTimeout(step, 22);
  };
  step();
}

export function toast(e: Pick<LogEntry, "ch" | "kind" | "text" | "t"> & { who?: string | null }): void {
  const box = $("#toasts");
  const d = h("div", { className: "toast" + (e.kind === "alert" ? " alert" : "") });
  if (e.who && PORTRAIT[e.who]) d.appendChild(pimg(e.who));
  const name = castName(e.who) || chName(e.ch);
  d.appendChild(h("div", {}, h("b", { text: `${name}${t("log.sep")}${clock(e.t)}` }), h("div", { text: e.text })));
  box.appendChild(d);
  while (box.children.length > 3) box.firstChild?.remove();
  setTimeout(() => d.remove(), e.kind === "alert" ? 9000 : 6500);
}

/** Quick narration toast that isn't logged (e.g. "you already checked that"). */
export const note = (text: string, ch = "sys"): void => toast({ ch, kind: "narr", text, t: S.t });
